export interface TaskSessionTreeEntry {
    key: string
    nestSessionId?: string
    parentSessionId?: string
    role?: string
    status?: string
    createdAt?: string
    updatedAt?: string
    lastUsedAt?: string
}

export interface TaskSessionTreeNode<T extends TaskSessionTreeEntry> {
    session: T
    children: TaskSessionTreeNode<T>[]
}

export function buildTaskSessionTree<T extends TaskSessionTreeEntry>(sessions: readonly T[]): TaskSessionTreeNode<T>[] {
    const entries = sessions.map((session, index) => ({ session, index, activityTime: sessionActivityTime(session) }))
    const bySessionId = new Map<string, (typeof entries)[number]>()
    for (const entry of entries) {
        if (entry.session.nestSessionId && !bySessionId.has(entry.session.nestSessionId)) {
            bySessionId.set(entry.session.nestSessionId, entry)
        }
    }

    const childrenByEntry = new Map<(typeof entries)[number], Array<(typeof entries)[number]>>()
    const parentByEntry = new Map<(typeof entries)[number], (typeof entries)[number]>()
    for (const entry of entries) {
        const parentId = entry.session.parentSessionId
        const parent = parentId ? bySessionId.get(parentId) : undefined
        if (!parent || parent === entry) continue
        parentByEntry.set(entry, parent)
        const children = childrenByEntry.get(parent) ?? []
        children.push(entry)
        childrenByEntry.set(parent, children)
    }

    interface RankedNode {
        node: TaskSessionTreeNode<T>
        active: boolean
        activityTime: number | null
        sourceIndex: number
    }

    const compareRank = (left: RankedNode, right: RankedNode): number => {
        if (left.active !== right.active) return left.active ? -1 : 1
        return descendingActivityOrder(left.activityTime, right.activityTime) || left.sourceIndex - right.sourceIndex
    }

    const visited = new Set<(typeof entries)[number]>()
    const materialize = (entry: (typeof entries)[number]): RankedNode | null => {
        if (visited.has(entry)) return null
        visited.add(entry)
        const children = (childrenByEntry.get(entry) ?? []).flatMap((child) => {
            const node = materialize(child)
            return node ? [node] : []
        })
        children.sort(compareRank)
        let activityTime = entry.activityTime
        for (const child of children) {
            if (child.activityTime !== null && (activityTime === null || child.activityTime > activityTime)) activityTime = child.activityTime
        }
        return {
            node: { session: entry.session, children: children.map((child) => child.node) },
            active: isTaskSessionActive(entry.session.status) || children.some((child) => child.active),
            activityTime,
            sourceIndex: entry.index,
        }
    }

    const roots: RankedNode[] = []
    for (const entry of entries) {
        if (parentByEntry.has(entry)) continue
        const root = materialize(entry)
        if (root) roots.push(root)
    }
    for (const entry of entries) {
        const root = materialize(entry)
        if (root) roots.push(root)
    }
    roots.sort((left, right) => sessionRoleOrder(left.node.session.role) - sessionRoleOrder(right.node.session.role) || compareRank(left, right))
    return roots.map((root) => root.node)
}

export function isTaskSessionActive(status: string | undefined): boolean {
    return status === "queued" || status === "waiting" || status === "starting" || status === "running"
}

function sessionRoleOrder(role: string | undefined): number {
    return role === "primary" ? 0 : 1
}

function sessionActivityTime(session: TaskSessionTreeEntry): number | null {
    let latest: number | null = null
    for (const value of [session.updatedAt, session.lastUsedAt, session.createdAt]) {
        if (!value) continue
        const timestamp = Date.parse(value)
        if (!Number.isFinite(timestamp)) continue
        latest = latest === null ? timestamp : Math.max(latest, timestamp)
    }
    return latest
}

function descendingActivityOrder(left: number | null, right: number | null): number {
    if (left === right) return 0
    if (left === null) return 1
    if (right === null) return -1
    return right - left
}
